/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { NOT_APPLICABLE } from '@config/constants/common';
import { TSite } from '@global-types/index';
import { apiClient } from '@lib/api-client';
import { QueryConfig } from '@lib/react-query';
import { TConversionFactor } from '@features/resources/conversion-factors/types';
import useStore from '@store/index';
import {
	queryOptions,
	useQuery,
} from '@tanstack/react-query';
import { getCountryName } from '@utils/countries';
import { formatTokenAvailable } from '@utils/formats';
import {
	convertGCO2EToTCO2EWithoutUnit,
	formatDate,
} from '@utils/functions';
import { getTechnologyProject } from '@utils/project-technologies';
import { getUnitLabel } from '@utils/units';
import { useTranslation } from 'react-i18next';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
type UseAdditionalityProjectOptions = {
	projectId: string;
	queryConfig?: QueryConfig<
		typeof getAdditionalityProjectQueryOptions
	>;
};

type TApiResponse = {
	entity: string;
	createdBy: string;
	filename: string;
	mimetype: string;
	extension: string;
	size_b: number;
	bucket: string;
	key: string;
	type: 'PICTURE' | 'VIDEO' | 'DOCUMENT'; // a
	link: string;
	acl: string;
	organization: string;
	createdAt: string;
	updatedAt: string;
	id: string;
};

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */

export const getAdditionalityProjectById = (
	projectId: string
): Promise<any> => {
	return apiClient.get(`/additionalities/${projectId}`, {
		params: {
			populate:
				'organization, entity, organization.additionalities, organization.additionalities.sites, organization.measurabilities.sites.conversionFactors, organization.measurabilities.sites.meterPoints, organization.entities, organization.entities.projects, organization.entities.projects.sites, organization.sites, organization.sites.conversionFactors, organization.sites.meterPoints, publicSpecs, internalSpecs',
		},
	});
};

export const getPublicAdditionalityProjectById = (
	projectId: string
): Promise<any> => {
	return apiClient.get(
		`/public/additionalities/${projectId}`,
		{
			params: {
				populate:
					'organization, entity, organization.additionalities, organization.additionalities.sites, organization.measurabilities.sites.conversionFactors, organization.measurabilities.sites.meterPoints, organization.entities, organization.entities.projects, organization.entities.projects.sites, organization.sites, organization.sites.conversionFactors, organization.sites.meterPoints, publicSpecs, internalSpecs, supportedBy.logo',
			},
		}
	);
};

export const getMedia = (
	id: string
): Promise<{
	media: TApiResponse;
}> => {
	return apiClient.get(`/public/media/${id}`);
};

export const getAdditionalityProjectQueryOptions = (
	projectId: string,
	user: any
) => {
	const { checkRole } = useAuthorization();
	return queryOptions({
		queryKey: [
			'get-additionality-project-update',
			projectId,
		],
		queryFn: () => {
			if (
				!checkRole([
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
				])
			) {
				return getPublicAdditionalityProjectById(
					projectId
				);
			}
			return getAdditionalityProjectById(projectId);
		},
		staleTime: 1000,
	});
};

export const getUserById = (
	userId: string
): Promise<any> => {
	return apiClient.get(`/users/${userId}`);
};

export const getUserQueryOptions = (userId: string) => {
	return queryOptions({
		queryKey: ['user-id', userId],
		queryFn: () => getUserById(userId),
	});
};

export const getOrganizationById = (
	organizationId: string
): Promise<any> => {
	return apiClient.get(
		`/organizations/${organizationId}`
	);
};

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */
export const useAdditionalityProject = ({
	projectId,
	queryConfig,
}: UseAdditionalityProjectOptions) => {
	const { getUser } = useStore();
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const userData: any = getUser();

	const { t } = useTranslation();
	const {
		data,
		isFetching,
		isFetched,
		isRefetching,
		isError,
		isLoading,
		isSuccess,
	} = useQuery({
		...getAdditionalityProjectQueryOptions(
			projectId,
			userData
		),
		...queryConfig,
	});

	let countryName = '';
	let technolgyProject: any = '';
	let sellPrice = '';
	let cover: any = '';
	let logo: any = '';
	let sites = [];

	if (data && isFetched && !isError) {
		if (data.technology) {
			technolgyProject = getTechnologyProject(
				data.technology
			);
		}

		cover = data && data.cover ? data.cover : null;

		logo = data && data.logo ? data.logo : null;
		if (data.location && data.location.countryCode)
			countryName = getCountryName(
				data.location.countryCode
			);

		if (!isNaN(data.sellPrice_europertco2eq))
			sellPrice = `${data.sellPrice_europertco2eq} ${t('global.sellPriceUnit')}`;

		if (
			data.organization &&
			data.organization.measurabilities &&
			Array.isArray(data.organization.measurabilities) &&
			data.organization.measurabilities.find(
				(item: TSite) => item.id == projectId
			)
		) {
			sites = data.organization.measurabilities
				.find((item: TSite) => item.id == projectId)
				.sites.map((_site: TSite) => {
					return {
						id: _site.id,
						name: _site.name,
						unit: _site.unit
							? getUnitLabel(_site.unit.toLowerCase())
							: NOT_APPLICABLE,
						place: _site.location
							? _site.location.place
							: NOT_APPLICABLE,
						countryCode: _site.location
							? _site.location.countryCode
							: NOT_APPLICABLE,
						conversionFactors:
							_site.conversionFactors &&
							Array.isArray(_site.conversionFactors)
								? _site.conversionFactors
										.filter(
											(
												_conversionFactor: TConversionFactor
											) =>
												_conversionFactor.startDate !==
												'1970-01-01T00:00:00.000Z'
										)
										.map(
											(
												_conversionFactor: TConversionFactor
											) => {
												return {
													id: _conversionFactor.id,
													ratio: _conversionFactor.ratio,
													startDate:
														_conversionFactor.startDate
															? formatDate(
																	_conversionFactor.startDate,
																	'DD/MM/YYYY'
																)
															: NOT_APPLICABLE,
													unit: _conversionFactor.unit
														? getUnitLabel(
																_conversionFactor.unit.toLowerCase()
															)
														: NOT_APPLICABLE,
												};
											}
										)
								: [],
						meterPoints:
							_site.meterPoints &&
							Array.isArray(_site.meterPoints)
								? _site.meterPoints.map(
										(_meterPoint: any) => {
											return {
												id: _meterPoint.id,
												serialNumber:
													_meterPoint.serialNumber
														? _meterPoint.serialNumber
														: NOT_APPLICABLE,
												countryCode: _meterPoint.location
													? _meterPoint.location
															.countryCode
													: NOT_APPLICABLE,
												place: _meterPoint.location
													? _meterPoint.location.place
													: NOT_APPLICABLE,
												unit: _meterPoint.unit
													? getUnitLabel(
															_meterPoint.unit.toLowerCase()
														)
													: NOT_APPLICABLE,
											};
										}
									)
								: [],
					};
				});
		}
	}

	const { data: user } = useQuery({
		queryKey: [
			'user',
			data &&
				data.organization &&
				data.organization.owner,
		],
		queryFn: () => getUserById(data.organization.owner),
		enabled:
			data && data.organization && data.organization.owner
				? !!data.organization
				: false,
	});

	const { data: contact } = useQuery({
		queryKey: [
			'contact',
			data && data.entity && data.entity.contact,
		],
		queryFn: () => getUserById(data.entity.contact),
		enabled:
			data && data.entity && data.entity.contact
				? !!data.entity.contact
				: false,
	});

	const { data: mediaCoverUrl } = useQuery({
		queryKey: ['get-media-cover-url', cover],
		queryFn: () => getMedia(cover),
		enabled: data && data.cover ? !!data.cover : false,
	});

	const { data: mediaLogo } = useQuery({
		queryKey: ['get-media-logo', logo],
		queryFn: () => getMedia(logo),
		enabled: data && data.logo ? !!data.logo : false,
	});

	return {
		data: {
			...data,
			country: {
				name: countryName,
				code: data?.location?.countryCode.toLowerCase(),
				place: data?.location?.place,
			},
			technology: technolgyProject.value
				? technolgyProject.value
				: NOT_APPLICABLE,
			sellPrice,
			cover,
			availableStock: `${
				data?.quantity?.remaining_aco2
					? formatTokenAvailable(
							convertGCO2EToTCO2EWithoutUnit(
								data.quantity.remaining_aco2
							)
						)
					: 0
			} ${getUnitLabel('tco2eq')}`,
			mediaLogo,
			mediaCoverUrl,
		},
		sites,
		user,
		contactProducer: contact,
		isFetching,
		isFetched,
		isRefetching,
		isError,
		isLoading,
		isSuccess,
	};
};
